/** @jsx jsx */
import { memo } from 'react';
import { css, jsx } from '@emotion/react';
import { Box, xcss } from '@atlaskit/primitives';
var baseStyles = xcss({
  display: 'inline-flex',
  borderRadius: 'border.radius',
  blockSize: 'min-content',
  position: 'static',
  overflow: 'hidden'
});
var textStyles = css({
  font: "var(--ds-font-body-small, normal 400 11px/16px ui-sans-serif, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Ubuntu, system-ui, \"Helvetica Neue\", sans-serif)",
  fontWeight: "var(--ds-font-weight-bold, 700)",
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap'
});
/**
 * __Lozenge__
 *
 * A lozenge is a visual indicator used to highlight an item's status for quick recognition.
 *
 * - [Examples](https://atlassian.design/components/lozenge/examples)
 * - [Code](https://atlassian.design/components/lozenge/code)
 * - [Usage](https://atlassian.design/components/lozenge/usage)
 */
var Lozenge = /*#__PURE__*/memo(function (_ref) {
  var children = _ref.children,
    testId = _ref.testId,
    _ref$isBold = _ref.isBold,
    isBold = _ref$isBold === void 0 ? false : _ref$isBold,
    _ref$appearance = _ref.appearance,
    appearance = _ref$appearance === void 0 ? 'default' : _ref$appearance,
    _ref$maxWidth = _ref.maxWidth,
    maxWidth = _ref$maxWidth === void 0 ? 200 : _ref$maxWidth,
    style = _ref.style;
  var appearanceStyle = isBold ? 'bold' : 'subtle';
  var appearanceType = appearance in backgroundColors[appearanceStyle] ? appearance : 'default';
  var maxWidthValue = typeof maxWidth === 'string' ? maxWidth : "".concat(maxWidth, "px");
  var maxWidthIsPc = typeof maxWidth === 'string' && /%$/.test(maxWidth);
  return jsx(Box, {
    as: "span",
    backgroundColor: backgroundColors[appearanceStyle][appearanceType],
    style: {
      backgroundColor: style === null || style === void 0 ? void 0 : style.backgroundColor,
      maxWidth: maxWidthIsPc ? maxWidth : '100%'
    },
    paddingInline: "space.050",
    xcss: baseStyles,
    testId: testId
  }, jsx("span", {
    css: [textStyles, textColors[appearanceStyle][appearanceType]],
    style: {
      color: style === null || style === void 0 ? void 0 : style.color,
      // to negate paddingInline specified on Box above
      maxWidth: maxWidthIsPc ? '100%' : "calc(".concat(maxWidthValue, " - ", "var(--ds-space-100, 8px)", ")")
    },
    "data-testid": testId && "".concat(testId, "--text")
  }, children));
});
Lozenge.displayName = 'Lozenge';
export default Lozenge;
var backgroundColors = {
  bold: {
    default: 'color.background.neutral.bold',
    inprogress: 'color.background.information.bold',
    moved: 'color.background.warning.bold',
    new: 'color.background.discovery.bold',
    removed: 'color.background.danger.bold',
    success: 'color.background.success.bold'
  },
  subtle: {
    default: 'color.background.neutral',
    inprogress: 'color.background.information',
    moved: 'color.background.warning',
    new: 'color.background.discovery',
    removed: 'color.background.danger',
    success: 'color.background.success'
  }
};
var textColors = {
  bold: {
    default: css({
      color: "var(--ds-text-inverse, #FFFFFF)"
    }),
    inprogress: css({
      color: "var(--ds-text-inverse, #FFFFFF)"
    }),
    moved: css({
      color: "var(--ds-text-warning-inverse, #172B4D)"
    }),
    new: css({
      color: "var(--ds-text-inverse, #FFFFFF)"
    }),
    removed: css({
      color: "var(--ds-text-inverse, #FFFFFF)"
    }),
    success: css({
      color: "var(--ds-text-inverse, #FFFFFF)"
    })
  },
  subtle: {
    default: css({
      color: "var(--ds-text-subtle, #42526E)"
    }),
    inprogress: css({
      color: "var(--ds-text-information, #0052CC)"
    }),
    moved: css({
      color: "var(--ds-text-warning, #974F0C)"
    }),
    new: css({
      color: "var(--ds-text-discovery, #403294)"
    }),
    removed: css({
      color: "var(--ds-text-danger, #DE350B)"
    }),
    success: css({
      color: "var(--ds-text-success, #006644)"
    })
  }
};